import * as React from 'react';
import { observer } from 'mobx-react';
import { TeamEmployeeErrorTable, EmployeeFieldInformation } from '@payscale/payscale-shared-ui/lib/TeamEmployeeErrorTable';
import { teamErrorData } from 'utils/MobxDataManager/singletons';
import Select from '@payscale/design/lib/select';
import './dataSummary.scss';
import { IEmployeeErrorData } from 'typings/employeeErrorData';
import { FieldDisplayName, DataSummaryTabs } from 'typings/enums';
import { escapeText } from 'utils/stringUtils';
import EmployeeEditModal from './employeeEditModal';
import { InviteUserModal } from './inviteUserModal';
import { IgnoreUserModal } from './ignoreUserModal';
import { IEmployeeIdMap } from 'typings/employee';
import DeletedEmployees from 'nomst/Components/Configuration/deletedEmployees';

interface DataSummaryState {
    errorData: IEmployeeErrorData[];
    filteredData: IEmployeeErrorData[];
    errorCounts: { [id: string]: number }
    selectOptions: [{ [id: string]: string }] | [];
    selectedField: string;
    requestingData: boolean;
    employeeIds: IEmployeeIdMap;
    showEditModal: boolean;
    showInviteModal: boolean;
    showIgnoreModal: boolean;
    selectedEmployeeErrorIndex: Nullable<number>;
    employeeForAction: Nullable<IUserInvite>;
    currentTab: DataSummaryTabs;
}

interface DataSummaryProps {

}

@observer
export default class DataSummary extends React.Component<DataSummaryProps, DataSummaryState> {

    constructor(props: DataSummaryProps) {
        super(props);
        this.onChangeFilter = this.onChangeFilter.bind(this);
        this.handleEditClick = this.handleEditClick.bind(this);
        this.downloadCsv = this.downloadCsv.bind(this);
        this.handleSubmitSuccess = this.handleSubmitSuccess.bind(this);
        this.isEmployeeLastOneWithoutManagerId = this.isEmployeeLastOneWithoutManagerId.bind(this);
        this.handleInviteUserClick = this.handleInviteUserClick.bind(this);
        this.handleIgnoreUserClick = this.handleIgnoreUserClick.bind(this);
        this.handleOpenUserInviteModal = this.handleOpenUserInviteModal.bind(this);
        this.handleOpenIgnoreUserModal = this.handleOpenIgnoreUserModal.bind(this);
        this.handleCancelClick = this.handleCancelClick.bind(this);
        this.handleErrorsTabClick = this.handleErrorsTabClick.bind(this);
        this.handleDeletedEmployeesTabClick = this.handleDeletedEmployeesTabClick.bind(this);

        this.state = {
            errorData: [],
            filteredData: [],
            errorCounts: {},
            selectOptions: [],
            selectedField: FieldDisplayName.All,
            requestingData: false,
            employeeIds: {},
            showEditModal: false,
            showInviteModal: false,
            showIgnoreModal: false,
            selectedEmployeeErrorIndex: null,
            employeeForAction: null,
            currentTab: DataSummaryTabs.DataErrors };
    }

    async componentDidMount(): Promise<void> {
        this.setState({requestingData: true});
        const errorData = await teamErrorData.requestEmployeeErrorData(true);
        const errorCounts = teamErrorData.countErrorData();
        const selectOptions = teamErrorData.createOptions();
        const employeeIds = await teamErrorData.getEmployeeIds();
        this.setState({
            employeeIds,
            errorCounts,
            selectOptions,
            errorData,
            filteredData: errorData,
            requestingData: false
        });
    }

    render(): React.ReactNode {
        const helpText = 'We\'ve spotted some missing data within Team. To resolve, edit the employee data or submit a refresh upload.';
        const isErrorsSelected = this.state.currentTab === DataSummaryTabs.DataErrors;
        const isEmployeesSelected = this.state.currentTab === DataSummaryTabs.DeletedEmployees;
        return <div className="nomst-data-summary" >
            <h1>Team Data Summary</h1>
            <div className="nomst-data-summary__error-container">
                <div className="nomst-data-summary__help-text">
                    {helpText}
                </div>
                <div className="nomst-data-summary__tabs tabs tabs--condensed">
                    <button
                        className={`nomst-data-summary__errors-tab tab ${isErrorsSelected ? 'active' : ''} pxl-link`}
                        onClick={this.handleErrorsTabClick}
                    >
                        Data Errors
                    </button>
                    <button
                        className={`nomst-data-summary__deleted-tab tab ${isEmployeesSelected ? 'active' : ''} pxl-link`}
                        onClick={this.handleDeletedEmployeesTabClick}
                    >
                        Deleted Employees
                    </button>
                </div>
                {this.renderTable()}
            </div>
            {this.renderEditEmployeeModal()}
            {this.renderInviteUserModal()}
            {this.renderIgnoreUserModal()}
        </div>;
    }

    private renderTable(): React.ReactNode {
        const { currentTab } = this.state;
        switch (currentTab) {
            case DataSummaryTabs.DataErrors:
                return this.renderDataErrors();
            case DataSummaryTabs.DeletedEmployees:
                return (
                    <DeletedEmployees
                        employees={teamErrorData.deletedEmployees}
                        deleteEmployeesCall={teamErrorData.deleteEmployees}
                    />
                );
        }
    }

    private renderDataErrors(): React.ReactNode {
        if (this.state.requestingData) {
            return null;
        }

        const noDataHeaderText = 'Great work! Your data is alright.';
        const noDataBodyText = 'If any gaps are found in your data, we\'ll surface them here to help you reslove the issues quickly.';
        if (!this.state.errorData.length) {
            return (
                <div className="nomst-data-summary__no-error-display">
                    <img
                        alt=""
                        src={require('contents/noErrorData.svg')}
                        className="no-error-display__image"
                    />
                    <h3 className="no-error-display__header">
                        {noDataHeaderText}
                    </h3>
                    <div className="no-error-display__body">
                        {noDataBodyText}
                    </div>
                </div>
            );
        }

        return (
            <>
                <div className="nomst-data-summary__filter-help-text">
                    Filter by Error Type:
                </div>
                <div className="nomst-data-summary__select-and-download-container">
                    <div className="nomst-data-summary__select-container">
                        <Select options={this.state.selectOptions} value={this.state.selectedField} onChange={this.onChangeFilter} />
                    </div>
                    <button
                        className="pxl-btn pxl-btn--primary nomst-data-summary__download-button"
                        onClick={this.downloadCsv}
                    >
                        Download CSV
                    </button>
                </div>
                <div className="nomst-data-summary__error-display">
                    <div className="nomst-data-summary__error-table-container">
                        <TeamEmployeeErrorTable
                            employeeData={this.state.filteredData}
                            onEditClickCallBack={this.handleEditClick}
                            ignoreUserCallBack={this.handleOpenIgnoreUserModal}
                            inviteUserCallBack={this.handleOpenUserInviteModal}
                        />
                    </div>
                    <div className="nomst-data-summary__field-information-container">
                        <EmployeeFieldInformation selectedField={this.state.selectedField} />
                    </div>
                </div>
            </>
        );
    }

    private onChangeFilter(_: undefined, value: string): void {
        this.setState({selectedField: value});
        this.filterOnError(value);
    }

    private filterOnError(fieldName: string): void {
        if (fieldName === FieldDisplayName.All) {
            return this.setState({filteredData: this.state.errorData});
        }
        const filteredData = this.state.errorData.filter(employee => !!employee.errorDetails[fieldName]);
        this.setState({filteredData});
    }

    private downloadCsv(): void {
        const downloadElement = window.document.createElement('a');
        let csv: string = ',Employee Name, Job Title, Employee Id, Hire Date, Manager Id, Email Address,' +
            'Pay Type, Standard Work Hours, Currency Code, Country Code, Field with Error\n';
        this.state.filteredData?.map(error => {
            Object.keys(error.errorDetails)?.map(detail => {
                let employeeDetails = this.createEmployeeRow(error);
                employeeDetails += `${escapeText(detail)}\n`;
                csv += employeeDetails;
            });
        });
        downloadElement.href = 'data:text/csv;charset=utf-8' + encodeURI(csv);
        downloadElement.target = '_blank';
        downloadElement.download = 'TeamDataSummary.csv';
        downloadElement.click();
        downloadElement.remove();
    }

    private createEmployeeRow(employee: IEmployeeErrorData): string {
        const { labEmployee, teamEmployee } = employee;
        const name = `${escapeText(labEmployee.firstName ?? '')} ${escapeText(labEmployee.lastName ?? '')}`;
        let row = `${name},`;
        row += `${escapeText(labEmployee.orgJobTitle ?? '')},`;
        row += `${escapeText(labEmployee.employeeNumber ?? '')},`;
        row += `${escapeText(labEmployee.dateOfHire ? labEmployee.dateOfHire.toDateString() : '')},`;
        row += `${escapeText(labEmployee.managerNumber ?? '')},`;
        row += `${escapeText(teamEmployee.workEmail ?? '')},`;
        row += `${escapeText(labEmployee.annualOrHourly ?? '')},`;
        row += `${escapeText(labEmployee.standardWorkHours?.toString() ?? '')},`;
        row += `${escapeText(labEmployee.currency ?? '')},`;
        row += `${escapeText(teamEmployee.country ?? '')},`;
        return row;
    }

    private handleEditClick(index?: number): void {
        if(index != null) {
            this.setState({ showEditModal: true, selectedEmployeeErrorIndex: index });
        }
    }

    private async handleIgnoreUserClick(): Promise<void> {
        await teamErrorData.ignoreUser(this.state.employeeForAction);
        this.updateUserList();
        this.handleCancelClick();
    }

    private async handleInviteUserClick(): Promise<void> {
        const user = this.state.employeeForAction;
        if (user) {
            await teamErrorData.inviteUser(user);
            this.updateUserList();
        }
        this.handleCancelClick();
    }

    updateUserList(): void {
        const errorData = this.state.errorData;
        const id = this.state.employeeForAction?.employeeId ?? '';
    
        // need to remove the ignored/invited employee and re-count our errors and set our options
        const newData = errorData.filter(employee => employee.labEmployee.employeeNumber !== id);
        teamErrorData.setNewErrorData(newData);
        const errorCounts = teamErrorData.countErrorData();
        const selectOptions = teamErrorData.createOptions();
        const filteredData = this.state.selectedField === FieldDisplayName.All ? newData :
            newData.filter(employee => !!employee.errorDetails[this.state.selectedField]);
        this.setState({errorData: newData, errorCounts, selectOptions, filteredData});
        this.handleCancelClick();
    }

    private handleCancelClick(): void {
        this.setState({
            showInviteModal: false,
            employeeForAction: null,
            showIgnoreModal: false,
            selectedEmployeeErrorIndex: null,
            showEditModal: false});
    }

    private handleOpenUserInviteModal(user: IUserInvite): void {
        this.setState({showInviteModal: true, employeeForAction: user});
    }

    private handleOpenIgnoreUserModal(user: IUserInvite): void {
        this.setState({showIgnoreModal: true, employeeForAction: user});
    }

    private renderEditEmployeeModal(): React.ReactNode {
        if (!this.state.showEditModal) {
            return null;
        }

        const employeeErrorData = this.state.selectedEmployeeErrorIndex != null && this.state.filteredData[this.state.selectedEmployeeErrorIndex];

        return (
            employeeErrorData &&
            <EmployeeEditModal
                employeeIds={this.state.employeeIds}
                employeeErrorData={employeeErrorData}
                onCancelFn={this.handleCancelClick}
                onSubmitSuccessCallBackFn={this.handleSubmitSuccess}
                isEmployeeLastOneWithoutManagerId={this.isEmployeeLastOneWithoutManagerId}
                allEmails={teamErrorData._allEmails}
            />
        );
    }

    private renderInviteUserModal(): React.ReactNode {
        if (!this.state.showInviteModal) {
            return null;
        }
        return (
            <InviteUserModal
                user={this.state.employeeForAction}
                onConfirmClick={this.handleInviteUserClick}
                onCloseClick={this.handleCancelClick}
            />
        );
    }

    private renderIgnoreUserModal(): React.ReactNode {
        if (!this.state.showIgnoreModal) {
            return null;
        }
        return (
            <IgnoreUserModal
                onConfirmClick={this.handleIgnoreUserClick}
                onCloseClick={this.handleCancelClick}
            />
        );
    }


    private async handleSubmitSuccess(): Promise<void> {
        if (this.state.selectedEmployeeErrorIndex == null) {
            return;
        }
        const errorData = teamErrorData.updateErrorCountOnSubmitSuccess(this.state.selectedEmployeeErrorIndex);
        const filteredData = this.state.selectedField === FieldDisplayName.All ? errorData :
            errorData.filter(employee => !!employee.errorDetails[this.state.selectedField]);
        const errorCounts = teamErrorData.countErrorData();
        const selectOptions = teamErrorData.createOptions();
        this.setState({ showEditModal: false, selectedEmployeeErrorIndex: null, selectOptions, filteredData, errorCounts });
    }

    private isEmployeeLastOneWithoutManagerId(): boolean {
        const { errorCounts } = this.state;
        return errorCounts.managerNumber != null ? errorCounts.managerNumber === 1 : true;
    }

    private handleErrorsTabClick(): void {
        this.setState({ currentTab: DataSummaryTabs.DataErrors });
    }

    private handleDeletedEmployeesTabClick(): void {
        this.setState({ currentTab: DataSummaryTabs.DeletedEmployees });
    }
}